import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { useLanguage } from '../context/LanguageContext';

export const Clients = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const { t } = useLanguage();

  const clients = [
    { name: 'Maxi Pizza Portugal', categoryKey: 'restaurant' },
    { name: 'Algarve Vacation', categoryKey: 'tourism' },
    { name: 'Atlantis Tours', categoryKey: 'tourism' },
    { name: 'The Art Studio Tavira', categoryKey: 'art' },
    { name: 'Au Comptoir Beauté', categoryKey: 'beauty' },
    { name: 'AllGarbe', categoryKey: 'services' },
    { name: 'Buy Algarve Properties', categoryKey: 'realestate' },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1],
      },
    },
  };

  return (
    <section 
      id="clientes" 
      data-testid="clients-section"
      ref={ref}
      className="py-24 md:py-32 lg:py-40"
    >
      <div className="container-main">
        {/* Section header */}
        <div className="text-center max-w-3xl mx-auto mb-16 md:mb-20">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-xs md:text-sm font-medium uppercase tracking-widest text-[#ff00c7] mb-4 block"
          >
            {t.clients.badge}
          </motion.span>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
            data-testid="clients-title"
            className="font-['Syne'] text-3xl md:text-4xl lg:text-5xl font-bold text-[#242328] leading-tight mb-6"
          >
            {t.clients.title}
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-base md:text-lg text-[#242328]/70 leading-relaxed"
          >
            {t.clients.description}
          </motion.p>
        </div>

        {/* Clients grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6"
        >
          {clients.map((client, index) => (
            <motion.div
              key={client.name}
              variants={itemVariants}
              data-testid={`client-${index}`}
              className="group relative bg-white/60 backdrop-blur-sm border border-[#242328]/10 rounded-xl p-6 md:p-8 text-center hover:border-[#ff00c7] transition-colors duration-300"
            >
              {/* Client name as typography logo */}
              <div className="mb-2">
                <span className="font-['Syne'] text-lg md:text-xl font-bold text-[#242328] group-hover:text-[#ff00c7] transition-colors duration-300">
                  {client.name}
                </span>
              </div>
              
              {/* Category */}
              <span className="text-xs uppercase tracking-wider text-[#242328]/50">
                {t.clients.categories[client.categoryKey]}
              </span>
            </motion.div>
          ))}
        </motion.div>

        {/* Trust indicators */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="mt-16 md:mt-20 flex flex-wrap justify-center gap-8 md:gap-16"
        >
          <div className="text-center">
            <p className="font-['Syne'] text-4xl md:text-5xl font-bold text-[#242328]">7+</p>
            <p className="text-sm text-[#242328]/60 mt-1">{t.clients.stats.activeClients}</p>
          </div>
          <div className="text-center">
            <p className="font-['Syne'] text-4xl md:text-5xl font-bold text-[#242328]">100+</p>
            <p className="text-sm text-[#242328]/60 mt-1">{t.clients.stats.postsMonth}</p>
          </div>
          <div className="text-center">
            <p className="font-['Syne'] text-4xl md:text-5xl font-bold text-[#242328]">3+</p>
            <p className="text-sm text-[#242328]/60 mt-1">{t.clients.stats.yearsExperience}</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
